import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { selectIsOpened, toggleWidget } from 'store/widget'
import { messagesActions, messagesSelectors } from 'store/messages'
import { settingsSelectors } from 'store/settings'
import { chatIsToRead } from 'utils/chats'
import { store } from 'store/index'
import { useConfigureChat } from './WidgetLayout'
import { Icon } from 'components/ui/Icon'

export const WidgetLauncher = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const isOpened = useSelector(selectIsOpened)
  const chats = useSelector(messagesSelectors.chats)
  const currentUser = useSelector(settingsSelectors.userDetails)
  const initializeChat = useConfigureChat()

  // True when at least one chat has an unread message (e.g. an operator replied to a ticket).
  const hasUnread = useMemo(
    () => Object.values(chats).some((chat) => chatIsToRead(chat, currentUser)),
    [chats, currentUser],
  )

  // Timestamp of the most recent unread message. Changes every time a new unread message arrives,
  // which lets us replay the bounce animation even when the dot is already showing.
  const unreadSignature = useMemo(
    () =>
      Object.values(chats)
        .filter((chat) => chatIsToRead(chat, currentUser))
        .reduce((max, chat) => Math.max(max, Number(chat.lastMessage?.timestamp ?? 0)), 0),
    [chats, currentUser],
  )

  // Briefly bounce the launcher whenever a new unread message comes in while the widget is closed.
  // The bounce lives on an inner element so it never clashes with the outer entrance animation.
  const [bounce, setBounce] = useState(false)
  useEffect(() => {
    if (!hasUnread || isOpened) return
    setBounce(true)
    const timeout = setTimeout(() => setBounce(false), 900)
    return () => clearTimeout(timeout)
  }, [unreadSignature, hasUnread, isOpened])

  const onClick = (e: React.MouseEvent) => {
    e.preventDefault()
    dispatch(toggleWidget())

    setTimeout(() => {
      const newIsOpened = store.getState().widgetState.isOpened
      if (newIsOpened) {
        initializeChat().catch(console.error)
      } else {
        // Closing the widget: drop the open chat so reopening lands back on the chat list, not a stale detail.
        dispatch(messagesActions.setChatId(undefined))
      }
    }, 10)
  }

  return (
    // Outer element: fixed positioning + one-time entrance animation (kept isolated from the bounce).
    <div className="animate-bounce-in tw-fixed tw-right-5 tw-bottom-5 tw-xs:right-2">
      <button
        type="button"
        aria-label={isOpened ? t('close') : t('needHelp')}
        aria-expanded={isOpened}
        className={`tw-relative tw-inline-flex tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-p-4 tw-text-white tw-shadow-lg focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 ${
          bounce ? 'animate-widget-bounce' : ''
        }`}
        style={{
          background: 'linear-gradient(95deg, var(--primary-gradient-start), var(--primary-gradient-end))',
        }}
        onClick={onClick}
      >
        <div
          className={`tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 icon-rotate-transition ${isOpened ? 'icon-rotate-90' : ''}`}
        >
          <Icon icon={isOpened ? ['far', 'xmark'] : ['far', 'comment']} style={{ color: 'white', fontSize: '24px' }} />
        </div>
        {hasUnread && !isOpened && (
          <span className="tw-absolute tw-top-1 tw-right-1 tw-h-3 tw-w-3 tw-rounded-full tw-bg-green-500 tw-ring-2 tw-ring-white" />
        )}
      </button>
    </div>
  )
}
